import {useCallback, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import {AlertCircle} from "lucide-react";
import ConfirmDialog from "@/components/forms/ConfirmDialog.tsx";
import DetailPaneState from "@/components/management/DetailPaneState.tsx";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {useUnsavedChangesGuard} from "@/hooks/useUnsavedChangesGuard.ts";
import {Scene} from "@/scenes/Scene.ts";
import SceneForm from "@/scenes/SceneForm.tsx";
import {useSaveSceneMutation, useSceneQuery} from "@/scenes/SceneQueries.ts";

export default function SceneEditor({mode}: {mode: "create" | "edit"}) {
    const {id} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {campaign, campaignPath} = useCampaign();
    const [dirty, setDirty] = useState(false);
    const query = useSceneQuery(mode === "edit" ? id : undefined);
    const saveMutation = useSaveSceneMutation();
    const {run: guardNavigation, dialogProps} = useUnsavedChangesGuard(dirty, mode === "create" ? {
        title: "Neue Szene verwerfen?",
        description: "Die bereits vorgenommenen Eingaben gehen verloren.",
        confirm: "Eingaben verwerfen",
    } : undefined);
    const scene = query.data?.campaignId === campaign.id ? query.data : undefined;

    const showScene = useCallback((saved: Scene) => {
        setDirty(false);
        navigate({pathname: campaignPath(`scenes/${saved.id}`), search: location.search}, {replace: true});
    }, [campaignPath, location.search, navigate]);

    const cancel = useCallback(() => guardNavigation(() => {
        setDirty(false);
        const target = mode === "edit" && id ? `scenes/${id}` : "scenes";
        navigate({pathname: campaignPath(target), search: location.search}, {replace: true});
    }), [campaignPath, guardNavigation, id, location.search, mode, navigate]);

    if (mode === "edit" && query.isPending) return <DetailPaneState title="Szene wird geladen…" loading/>;
    if (mode === "edit" && query.isError) return <DetailPaneState title="Szene konnte nicht geladen werden" onRetry={() => void query.refetch()}/>;
    if (mode === "edit" && !scene) return <DetailPaneState title="Szene nicht verfügbar"
                                                            description="Die Szene gehört nicht zu dieser Kampagne."
                                                            icon={<AlertCircle className="size-10"/>}/>;

    return (
        <>
            <title>{mode === "create" ? "Neue Szene | DireDawn" : `${scene!.name} bearbeiten | DireDawn`}</title>
            <ConfirmDialog {...dialogProps}/>
            <SceneForm key={scene?.id ?? campaign.id}
                       scene={scene}
                       campaignId={campaign.id}
                       campaignTitle={campaign.title}
                       onSubmit={saveMutation.mutateAsync}
                       onSaved={showScene}
                       onCancel={cancel}
                       onDirtyChange={setDirty}/>
        </>
    );
}
